import React, { useEffect, useState } from "react";
import { apiService } from "../services/apiService";
import {
  FaGlobe,
  FaMapMarkerAlt,
  FaFlag,
  FaClock,
  FaShieldAlt,
  FaUsers,
  FaEye,
} from "react-icons/fa";

const AHome = () => {
  const [traffic, setTraffic] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchTrafficData();
  }, []);

  const fetchTrafficData = async () => {
    try {
      const response = await apiService.userData.getAll();
      setTraffic(response.data);
    } catch (error) {
      console.error("Error fetching traffic data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isRecentVisitor = (dateString) => {
    const visitDate = new Date(dateString);
    const currentDate = new Date();
    const diffTime = currentDate - visitDate;
    const diffHours = diffTime / (1000 * 60 * 60);
    return diffHours <= 24;
  };

  const getThreatLevelColor = (threat) => {
    if (threat === "low") return "text-green-600 bg-green-100";
    if (threat === "medium") return "text-yellow-600 bg-yellow-100";
    if (threat === "high") return "text-red-600 bg-red-100";
    return "text-gray-600 bg-gray-100";
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <FaGlobe className="text-white text-xl mr-3" />
              <h1 className="text-xl font-bold text-white">
                Website Analytics
              </h1>
            </div>
            <div className="text-white text-sm">
              {traffic.length} visitor{traffic.length !== 1 ? "s" : ""}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <FaUsers className="text-blue-600 text-xl" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                Total Visitors
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {traffic.length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <FaEye className="text-green-600 text-xl" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                Recent Visitors
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {
                  traffic.filter((visit) => isRecentVisitor(visit.datePosted))
                    .length
                }
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <FaFlag className="text-yellow-600 text-xl" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Countries</p>
              <p className="text-2xl font-bold text-gray-900">
                {new Set(traffic.map((visit) => visit.country_name)).size}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center">
            <div className="p-3 bg-red-100 rounded-lg">
              <FaShieldAlt className="text-red-600 text-xl" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Threats</p>
              <p className="text-2xl font-bold text-gray-900">
                {traffic.filter((visit) => visit.threat !== "low").length}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4">
          <h2 className="text-xl font-bold text-white">Visitor Details</h2>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="text-center py-8">
              <svg
                className="animate-spin h-8 w-8 text-indigo-600 mx-auto"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <p className="mt-2 text-gray-600">Loading visitor data...</p>
            </div>
          ) : traffic.length === 0 ? (
            <div className="text-center py-8">
              <FaGlobe className="text-gray-400 text-4xl mx-auto mb-4" />
              <p className="text-gray-600">No visitor data available</p>
            </div>
          ) : (
            <div className="space-y-4">
              {traffic.map((visit) => (
                <div
                  key={visit._id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                        <FaGlobe className="text-indigo-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {visit.ip}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {formatDate(visit.datePosted)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {isRecentVisitor(visit.datePosted) && (
                        <span className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                          Recent
                        </span>
                      )}
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getThreatLevelColor(
                          visit.threat
                        )}`}
                      >
                        {visit.threat} threat
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center">
                      <FaMapMarkerAlt className="text-gray-400 mr-2" />
                      <span className="text-gray-600">
                        {visit.city}, {visit.region}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <FaFlag className="text-gray-400 mr-2" />
                      <span className="text-gray-600">
                        {visit.country_name} {visit.emoji_flag}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <FaClock className="text-gray-400 mr-2" />
                      <span className="text-gray-600">{visit.time_zone}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AHome;
